/**
 * Regras da tela de capacidade das keys (limite de values do GAM).
 *
 * Puro de propósito: agrupar, ordenar e decidir o alerta é onde um erro faria
 * a tela mostrar folga que não existe, então fica testável fora do React.
 */
import type { KeyCapacityItem, KeyCapacityLevel } from '@/types/custom-targeting';

/** Keys principais, na ordem em que aparecem. A variante `_hour` vai dentro do card. */
export const CAPACITY_MAIN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export interface CapacityCard {
    name: string;
    main?: KeyCapacityItem;
    hour?: KeyCapacityItem;
    /** O pior nível entre a key e a variante `_hour`: é ele que colore o card. */
    level: KeyCapacityLevel;
}

const GRAVIDADE: Record<KeyCapacityLevel, number> = { ok: 0, desconhecido: 1, atencao: 2, critico: 3 };

export function worstLevel(levels: KeyCapacityLevel[]): KeyCapacityLevel {
    return levels.reduce<KeyCapacityLevel>(
        (pior, atual) => (GRAVIDADE[atual] > GRAVIDADE[pior] ? atual : pior),
        'ok'
    );
}

/** Um card por key principal, com a `_hour` junto. Key que não existe na base some. */
export function buildCapacityCards(items: KeyCapacityItem[]): CapacityCard[] {
    const porNome = new Map(items.map((item) => [item.name, item]));
    return CAPACITY_MAIN_KEYS
        .map((name) => {
            const main = porNome.get(name);
            const hour = porNome.get(`${name}_hour`);
            const existentes = [main, hour].filter((i): i is KeyCapacityItem => !!i && i.exists);
            return { name, main: main?.exists ? main : undefined, hour: hour?.exists ? hour : undefined, level: worstLevel(existentes.map((i) => i.level)) };
        })
        .filter((card) => card.main || card.hour);
}

/** Keys em atenção ou crítico, da mais cheia para a menos cheia. */
export function capacityAlerts(items: KeyCapacityItem[]): KeyCapacityItem[] {
    return items
        .filter((item) => item.exists && (item.level === 'critico' || item.level === 'atencao'))
        .sort((a, b) => (b.percent ?? 0) - (a.percent ?? 0));
}

/** Largura da barra: nunca passa de 100% visualmente, mesmo com a key acima do limite. */
export function barWidth(percent?: number | null): number {
    if (percent === null || percent === undefined || Number.isNaN(percent)) return 0;
    return Math.max(0, Math.min(100, percent));
}

export function formatCount(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('pt-BR');
}

export function formatPercent(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** "100 mil", "1,2 mi" — para o limite, que não precisa de precisão. */
export function formatCompact(value: number): string {
    return new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function plural(n: number, singular: string, pluralForm: string): string {
    return `${n.toLocaleString('pt-BR')} ${n === 1 ? singular : pluralForm}`;
}

export const LEVEL_STYLES: Record<
    KeyCapacityLevel,
    { bar: string; text: string; badge: string; dot: string; border: string; label: string }
> = {
    ok: {
        bar: 'bg-emerald-500', text: 'text-emerald-600', dot: 'bg-emerald-500', border: 'border-slate-200',
        badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', label: 'Com folga',
    },
    atencao: {
        bar: 'bg-amber-500', text: 'text-amber-600', dot: 'bg-amber-500', border: 'border-amber-300',
        badge: 'bg-amber-50 text-amber-700 ring-amber-600/20', label: 'Atenção',
    },
    critico: {
        bar: 'bg-rose-500', text: 'text-rose-600', dot: 'bg-rose-500', border: 'border-rose-300',
        badge: 'bg-rose-50 text-rose-700 ring-rose-600/20', label: 'Crítico',
    },
    desconhecido: {
        bar: 'bg-slate-300', text: 'text-slate-500', dot: 'bg-slate-300', border: 'border-slate-200',
        badge: 'bg-slate-50 text-slate-600 ring-slate-500/20', label: 'Sem leitura',
    },
};
